import { memo, useMemo } from "react"

import type {
  AnimationEvent,
  BallPositionChange,
  MovePositionChange,
  PositionChange,
  StatusPositionChange,
} from "@/types"

import type { PitchState } from "./use-pitch-state"

const COLS = 26
const ROWS = 15

interface PitchOverlayProps {
  event: AnimationEvent | null
  actionColor: string | null
  pitchState: PitchState
}

type Point = [number, number]

// Cell center in SVG coordinates
const cx = (col: number) => col + 0.5
const cy = (row: number) => row + 0.5

const svgStyle: React.CSSProperties = {
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  pointerEvents: "none",
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getActionType(event: AnimationEvent): string | null {
  const header = event.detailHeader
  if (
    typeof header === "object" &&
    header !== null &&
    "action_type" in header
  ) {
    return header.action_type ?? null
  }
  return null
}

function findPlayerPosition(
  pitchState: PitchState,
  playerId: string,
): Point | null {
  for (const [key, player] of pitchState.playerPositions) {
    if (player.playerId === playerId) {
      const parts = key.split(",")
      return [Number(parts[0]), Number(parts[1])]
    }
  }
  return null
}

// Filters position changes by discriminant, narrowing to the matching union
// member so callers get typed `to`/`from`/`status` fields without casts.
function ofType(
  changes: PositionChange[],
  type: "move" | "push" | "follow_up",
): MovePositionChange[]
function ofType(
  changes: PositionChange[],
  type: "status",
): StatusPositionChange[]
function ofType(changes: PositionChange[], type: "ball"): BallPositionChange[]
function ofType(
  changes: PositionChange[],
  type: PositionChange["type"],
): PositionChange[] {
  return changes.filter((c) => c.type === type)
}

/** Build ordered waypoint path from a sequence of move changes. */
function buildMovePath(moveChanges: MovePositionChange[]): Point[] {
  if (moveChanges.length === 0) return []
  const path: Point[] = []
  for (const change of moveChanges) {
    if (path.length === 0 && change.from) {
      path.push(change.from)
    }
    path.push(change.to)
  }
  return path
}

/** Find the block target's position (before push). */
function findBlockTarget(
  event: AnimationEvent,
  pitchState: PitchState,
): Point | null {
  // Target was at push.from before being pushed
  const pushes = ofType(event.positionChanges, "push")
  if (pushes.length > 0 && pushes[0].from) {
    return pushes[0].from
  }
  // Fallback: status change on a player other than the acting player
  const statusChanges = ofType(event.positionChanges, "status")
  for (const sc of statusChanges) {
    if (sc.playerId && sc.playerId !== event.actingPlayerId) {
      const pos = findPlayerPosition(pitchState, sc.playerId)
      if (pos) return pos
    }
  }
  return null
}

/**
 * Find the blocker's position before any follow-up movement.
 * Priority: follow_up.from > end of move path > current pitchState position.
 */
function findBlockerPosition(
  event: AnimationEvent,
  pitchState: PitchState,
  movePath: Point[] = [],
): Point | null {
  const followUps = ofType(event.positionChanges, "follow_up")
  if (followUps.length > 0 && followUps[0].from) {
    return followUps[0].from
  }
  if (movePath.length > 0) {
    return movePath[movePath.length - 1]
  }
  if (event.actingPlayerId) {
    return findPlayerPosition(pitchState, event.actingPlayerId)
  }
  return null
}

// ─── SVG Primitives ───────────────────────────────────────────────────────────

/** Arrow: line with triangular arrowhead. */
function Arrow({
  from,
  to,
  color,
  strokeWidth,
  opacity,
  headSize = 0.2,
}: {
  from: Point
  to: Point
  color: string
  strokeWidth: number
  opacity: number
  headSize?: number
}) {
  const x1 = cx(from[0]),
    y1 = cy(from[1])
  const x2 = cx(to[0]),
    y2 = cy(to[1])
  const dx = x2 - x1,
    dy = y2 - y1
  const len = Math.sqrt(dx * dx + dy * dy)
  if (len === 0) return null

  const ux = dx / len,
    uy = dy / len
  const nx = -uy,
    ny = ux

  const tipX = x2,
    tipY = y2
  const b1x = tipX - ux * headSize + nx * headSize * 0.5
  const b1y = tipY - uy * headSize + ny * headSize * 0.5
  const b2x = tipX - ux * headSize - nx * headSize * 0.5
  const b2y = tipY - uy * headSize - ny * headSize * 0.5

  return (
    <g>
      <line
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        stroke={color}
        strokeWidth={strokeWidth}
        opacity={opacity}
        strokeLinecap="round"
      />
      <polygon
        points={`${tipX},${tipY} ${b1x},${b1y} ${b2x},${b2y}`}
        fill={color}
        opacity={opacity}
      />
    </g>
  )
}

/** Movement path: dots at waypoints connected by a dashed line. */
function MovePath({
  path,
  color,
  bold = false,
}: {
  path: Point[]
  color: string
  bold?: boolean
}) {
  if (path.length < 2) return null

  const sw = bold ? 0.12 : 0.08
  const r = bold ? 0.18 : 0.15
  const points = path.map((p) => `${cx(p[0])},${cy(p[1])}`).join(" ")

  return (
    <g>
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth={sw}
        strokeDasharray={bold ? "0.15 0.1" : "0.12 0.08"}
        strokeLinecap="round"
        opacity={0.7}
      />
      {path.map((p, i) => (
        <circle
          key={i}
          cx={cx(p[0])}
          cy={cy(p[1])}
          r={r}
          fill={color}
          opacity={0.7}
        />
      ))}
    </g>
  )
}

// ─── Overlay Layers ───────────────────────────────────────────────────────────

/**
 * Under-players overlay: wide translucent push arrows.
 * Rendered between field markings and the player grid so tokens appear on top.
 */
export const PitchOverlayUnder = memo(function PitchOverlayUnder({
  event,
  actionColor,
}: PitchOverlayProps) {
  const elements = useMemo(() => {
    if (!event || !actionColor || event.pitchSnapshot) return null

    const pushes = ofType(event.positionChanges, "push")
    if (pushes.length === 0) return null

    return pushes
      .map((push, i) => {
        if (!push.from || !push.to || !Array.isArray(push.to)) return null
        return (
          <Arrow
            key={i}
            from={push.from}
            to={push.to}
            color={actionColor}
            strokeWidth={0.25}
            opacity={0.25}
            headSize={0.3}
          />
        )
      })
      .filter(Boolean)
  }, [event, actionColor])

  if (!elements || elements.length === 0) return null

  return (
    <svg
      viewBox={`0 0 ${COLS} ${ROWS}`}
      preserveAspectRatio="none"
      style={svgStyle}
    >
      {elements}
    </svg>
  )
})

/**
 * Over-players overlay: movement paths, block arrows, blitz overlays, follow-up arrows.
 * Rendered above the player grid.
 */
export const PitchOverlayOver = memo(function PitchOverlayOver({
  event,
  actionColor,
  pitchState,
}: PitchOverlayProps) {
  const elements = useMemo(() => {
    if (!event || !actionColor || event.pitchSnapshot) return null

    const actionType = getActionType(event)
    const result: React.ReactNode[] = []

    // ── Move ────────────────────────────────────────────────────────────
    if (actionType === "Move") {
      const path = buildMovePath(ofType(event.positionChanges, "move"))
      if (path.length >= 2) {
        result.push(<MovePath key="move" path={path} color={actionColor} />)
      }
    }

    // ── Block ───────────────────────────────────────────────────────────
    if (actionType === "Block") {
      const blockerPos = findBlockerPosition(event, pitchState)
      const targetPos = findBlockTarget(event, pitchState)
      if (blockerPos && targetPos) {
        result.push(
          <Arrow
            key="block"
            from={blockerPos}
            to={targetPos}
            color={actionColor}
            strokeWidth={0.12}
            opacity={0.6}
            headSize={0.25}
          />,
        )
      }
    }

    // ── Blitz ───────────────────────────────────────────────────────────
    if (actionType === "Blitz") {
      const movePath = buildMovePath(ofType(event.positionChanges, "move"))
      // Movement phase (bold dashed path)
      if (movePath.length >= 2) {
        result.push(
          <MovePath
            key="blitz-move"
            path={movePath}
            color={actionColor}
            bold
          />,
        )
      }
      // Block phase (aggressive arrowhead)
      const blockerPos = findBlockerPosition(event, pitchState, movePath)
      const targetPos = findBlockTarget(event, pitchState)
      if (blockerPos && targetPos) {
        result.push(
          <Arrow
            key="blitz-block"
            from={blockerPos}
            to={targetPos}
            color={actionColor}
            strokeWidth={0.14}
            opacity={0.7}
            headSize={0.28}
          />,
        )
      }
    }

    // ── Follow-up ───────────────────────────────────────────────────────
    const followUps = ofType(event.positionChanges, "follow_up")
    for (let i = 0; i < followUps.length; i++) {
      const fu = followUps[i]
      if (fu.from && fu.to && Array.isArray(fu.to)) {
        result.push(
          <Arrow
            key={`follow-up-${i}`}
            from={fu.from}
            to={fu.to}
            color={actionColor}
            strokeWidth={0.08}
            opacity={0.3}
            headSize={0.18}
          />,
        )
      }
    }

    return result.length > 0 ? result : null
  }, [event, actionColor, pitchState])

  if (!elements) return null

  return (
    <svg
      viewBox={`0 0 ${COLS} ${ROWS}`}
      preserveAspectRatio="none"
      style={svgStyle}
    >
      {elements}
    </svg>
  )
})
